import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { supabase } from "../config/supabaseClient";
import { fetchOrganogramByDepartmentId } from "./organogramSlice";

export const fetchAllUsers = createAsyncThunk(
  "users/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const { data, error } = await supabase.auth.admin.listUsers();
      if (error) throw error;
      return data.users; // 'users' array is inside 'data'
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateUser = createAsyncThunk(
  "users/updateUser",
  async ({ id, email, phone, name }, { rejectWithValue }) => {
    try {
      // Update the user in Supabase Auth
      const { data, error } = await supabase.auth.admin.updateUserById(id, {
        email,
        phone,
        user_metadata: { displayName: name },
      });

      if (error) throw error;
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Delete user
export const deleteUser = createAsyncThunk(
  "users/delete",
  async (id, { rejectWithValue }) => {
    try {
      // Delete user from Supabase Auth
      const { error } = await supabase.auth.admin.deleteUser(id);

      if (error) throw error;
      return id;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const getUserById = createAsyncThunk(
  "users/getUserById",
  async (id, { rejectWithValue }) => {
    try {
      const { data, error } = await supabase.auth.admin.getUserById(id);
      if (error) throw error;
      return data.user; // Return user data
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const getUserDetails = createAsyncThunk(
  "users/getUserDetails",
  async (id, { rejectWithValue, dispatch }) => {
    try {
      if (!id) return null;
      const { data, error } = await supabase
        .from("company_user")
        .select("*")
        .eq("id", id)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;

      let deptName = null;
      if (data.department_id) {
        const res = await dispatch(fetchOrganogramByDepartmentId(data.department_id));
        deptName = res?.payload ?? null;
      }
      data.department_name = deptName?.dept_name;

      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk to add a user
export const addUser = createAsyncThunk(
  "auth/addUser",
  async (userData, { rejectWithValue }) => {
    try {
      const { email, name, role, phoneNumber } = userData;

      // Invite user by email (They receive a "Set Password" email)
      const { data, error } = await supabase.auth.admin.inviteUserByEmail(
        email,
        // {
        //   redirectTo: `${process.env.REACT_APP_URL}/reset-password`,
        // }
      );

      if (error) throw error;

      // Store additional user metadata
      await supabase.auth.updateUser({
        data: {
          displayName: name,
          role: role || "user",
          phoneNumber: phoneNumber,
        },
      });

      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const userSlice = createSlice({
  name: "users",
  initialState: {
    users: [],
    loading: false,
    error: null,
    userDetails: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllUsers.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAllUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload;
      })
      .addCase(fetchAllUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(addUser.fulfilled, (state, action) => {
        state.users.push(action.payload);
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        const user = state.users.find((user) => user.id === action.payload.id);
        if (user) {
          user.status = action.payload.status;
        }
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.users = state.users.filter((user) => user.id !== action.payload);
      })
      .addCase(getUserDetails.pending, (state) => {
        state.loading = true;
      })
      .addCase(getUserDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.userDetails = action.payload;
      })
      .addCase(getUserDetails.rejected, (state, action) => {
        state.loading = false;
      });
  },
});
export default userSlice.reducer;
